import { computed, toValue, watch, type MaybeRefOrGetter } from "vue";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";
import type { SendAdminEmailRequest } from "@shopexperts/legacy-api";

export const ADMIN_EMAILS_QUERY_KEY = ["admin", "emails"] as const;

export function useAdminEmailFromProfiles(enabled: MaybeRefOrGetter<boolean> = true) {
  const api = useApi();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "email-from-profiles"],
    queryFn: () => api.admin.emails.listFromProfiles(),
    enabled: computed(() => toValue(enabled)),
    staleTime: 10 * 60 * 1000,
  });

  const profiles = computed(() => data.value ?? []);

  return { profiles, isLoading };
}

/** Reply-To address applied when an email is sent with reply_to_support. */
export function useAdminEmailReplyTo() {
  const api = useApi();

  const { data } = useQuery({
    queryKey: ["admin", "settings", "admin_emails"],
    queryFn: () => api.admin.settings.list({ query: { groups: ["admin_emails"] } }),
    staleTime: 10 * 60 * 1000,
  });

  const replyTo = computed(() => {
    const setting = data.value?.find((entry) => entry.key === "admin_emails.reply_to");
    return setting && typeof setting.value === "string" ? setting.value : null;
  });

  return { replyTo };
}

export interface AdminEmailsListFilters {
  search?: string;
  status?: string;
  from?: string;
  /** Matches emails carrying all of these tags. */
  tagIds?: number[];
  expertId?: number;
}

/** Email tag catalog, for the list's tag filter. */
export function useAdminEmailTagCatalog() {
  const api = useApi();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "email-tags"],
    queryFn: () => api.admin.emails.listTags(),
    staleTime: 5 * 60 * 1000,
  });

  const tags = computed(() => data.value ?? []);
  // Plain names: the select trigger shows the selection inline, and counts
  // there would crowd out the names it has to truncate anyway.
  const tagOptions = computed(() =>
    tags.value.map((tag) => ({ value: String(tag.id), label: tag.name })),
  );
  const tagNameById = computed(
    () => new Map(tags.value.map((tag) => [tag.id, tag.name])),
  );

  return { tags, tagOptions, tagNameById, isLoading };
}

export function useAdminEmailsList(filters: MaybeRefOrGetter<AdminEmailsListFilters> = {}) {
  const api = useApi();
  const toaster = useToaster();

  const query = computed(() => {
    const current = toValue(filters);
    return {
      expert_id: current.expertId || undefined,
      status: current.status || undefined,
      from: current.from || undefined,
      tag_ids: current.tagIds?.length ? current.tagIds : undefined,
      search: current.search?.trim() || undefined,
      limit: 25,
    };
  });

  const queryResult = useInfiniteQuery({
    queryKey: computed(() => [...ADMIN_EMAILS_QUERY_KEY, query.value]),
    queryFn: ({ pageParam }) =>
      api.admin.emails.list({
        query: query.value,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.has_next ? lastPage.pagination.cursor ?? undefined : undefined,
  });

  watch(queryResult.error, (error) => {
    if (error) toaster.create({ title: "Failed to load emails", type: "error" });
  });

  const emails = computed(() => queryResult.data.value?.pages.flatMap((page) => page.data) ?? []);
  const total = computed(() => queryResult.data.value?.pages[0]?.meta?.total ?? null);

  return {
    emails,
    total,
    isLoading: queryResult.isLoading,
    hasNextPage: queryResult.hasNextPage,
    fetchNextPage: queryResult.fetchNextPage,
    isFetchingNextPage: queryResult.isFetchingNextPage,
  };
}

export function useSendAdminEmail() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const sendEmail = useMutation({
    mutationFn: (body: SendAdminEmailRequest) => api.admin.emails.send({ body }),
    onSuccess: (_email, body) => {
      toaster.create({ title: "Email sent", type: "success" });
      queryClient.invalidateQueries({ queryKey: ADMIN_EMAILS_QUERY_KEY });
      // A send can mint new tags, so the catalog's counts are stale too.
      queryClient.invalidateQueries({ queryKey: ["admin", "email-tags"] });
      if (body.expert_tags?.length) {
        queryClient.invalidateQueries({ queryKey: ["admin", "experts"] });
        queryClient.invalidateQueries({ queryKey: ["admin", "expert"] });
        queryClient.invalidateQueries({ queryKey: ["admin", "expert-tags"] });
      }
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to send email: ${error.message}`, type: "error" });
    },
  });

  return { sendEmail };
}
